import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {CSSProperties} from 'react';

import {ReviewBadgeContext} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import _RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import ReviewActionButton from './ReviewActionButton';
import {ReviewInProgressMenu} from './ReviewInProgressMenu';
import ReviewSummaryPanelMenu from './ReviewSummaryPanelMenu';
import {
  ReviewControllerDomainAdapter,
  ReviewControllerResult,
  useReviewController,
} from './useReviewController';

type ReviewBadgePropsWithDataTestAttrs = ReviewBadgeProps &
  Partial<{
    'data-test-component': string;
    'data-test-element': string;
    'data-test-key': string;
    'data-test-index': string;
    'data-testid': string;
  }>;

function omitDataTestProps({
  'data-test-component': _dataTestComponent,
  'data-test-element': _dataTestElement,
  'data-test-key': _dataTestKey,
  'data-test-index': _dataTestIndex,
  'data-testid': _dataTestId,
  ...reviewBadgeProps
}: ReviewBadgePropsWithDataTestAttrs): ReviewBadgeProps {
  return reviewBadgeProps;
}

export {REVIEWABLE_QUERY, wrapOnReviewChangeWithRefetch} from './useReviewController';

export interface DefaultBadgeSpec {
  text?: string;
  icon?: BenchlingIconName;
  style?: CSSProperties;
}

export interface ReviewBadgeProps extends ReviewControllerDomainAdapter {
  className?: string;
  reviewableId: string;
  defaultBadgeSpec?: DefaultBadgeSpec;
  /** When provided, renders from an existing controller instead of issuing a separate reviewable query. */
  controller?: ReviewControllerResult;
}

interface ReviewBadgeViewProps {
  className?: string;
  defaultBadgeSpec?: DefaultBadgeSpec;
  controller: ReviewControllerResult;
}

function ReviewBadgeView({
  className,
  defaultBadgeSpec,
  controller,
}: ReviewBadgeViewProps): JSX.Element | null {
  if (controller.state === 'PENDING') {
    return (
      <div className={classNames(css(reviewStyles.throbber), className)}>
        <HorizontalThrobber />
      </div>
    );
  }

  if (controller.state === 'HIDDEN') {
    return null;
  }

  const {
    reviewStatus,
    terminalState,
    badgeStatus,
    isDropdown,
    showSummaryPanel,
    permissions,
    contextValue,
    dropdown,
    realtime,
  } = controller;

  const button = (
    <ReviewActionButton
      mode={badgeStatus}
      text={ReviewUtil.getStatusTextFromReview(
        reviewStatus,
        terminalState ?? undefined,
        defaultBadgeSpec?.text
      )}
      isDropdown={isDropdown}
      expanded={dropdown.isOpen}
      icon={
        <BenchlingIcon
          className="reviewBadge-icon"
          icon={ReviewUtil.getStatusIcon(reviewStatus, defaultBadgeSpec?.icon)}
        />
      }
      defaultBadgeStyle={defaultBadgeSpec?.style}
      className={className}
    />
  );

  let dropdownElement;
  if (isDropdown) {
    dropdownElement = (
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        isOpen={dropdown.isOpen}
        onToggle={dropdown.onToggle}
        menu={
          <ReviewBadgeContext.Provider value={contextValue}>
            {showSummaryPanel ? (
              <ReviewSummaryPanelMenu permissions={permissions} />
            ) : (
              <ReviewInProgressMenu
                disableOptions={dropdown.disableOptions}
                tooltipTitle={dropdown.tooltipTitle}
              />
            )}
          </ReviewBadgeContext.Provider>
        }
      >
        {button}
      </Dropdown>
    );
  }

  return (
    <ReviewBadgeContext.Provider value={contextValue}>
      <RealtimeUpdateSubscriptionWrapper
        apiId={realtime.apiId}
        refetch={realtime.refetch}
        messageFilter="no_filter"
      >
        {dropdownElement ?? button}
      </RealtimeUpdateSubscriptionWrapper>
    </ReviewBadgeContext.Provider>
  );
}

function ReviewBadgeWithController({
  reviewableId,
  className,
  defaultBadgeSpec,
  controller: _controller,
  ...domainAdapter
}: ReviewBadgeProps): JSX.Element | null {
  const controller = useReviewController(reviewableId, domainAdapter);
  return (
    <ReviewBadgeView controller={controller} defaultBadgeSpec={defaultBadgeSpec} className={className} />
  );
}

/**
 * Displays review status of a reviewable object.
 *
 * This is a thin view over {@link useReviewController}: the controller owns the reviewable query,
 * derivation, action wiring, and realtime refetch, while this component maps the returned data to the
 * badge button + dropdown presentation (text/icon/style via ReviewUtil and the optional defaultBadgeSpec).
 */
function ReviewBadge(props: ReviewBadgePropsWithDataTestAttrs): JSX.Element | null {
  const reviewBadgeProps = omitDataTestProps(props);

  if (reviewBadgeProps.controller != null) {
    return (
      <ReviewBadgeView
        controller={reviewBadgeProps.controller}
        defaultBadgeSpec={reviewBadgeProps.defaultBadgeSpec}
        className={reviewBadgeProps.className}
      />
    );
  }

  return <ReviewBadgeWithController {...reviewBadgeProps} />;
}

const reviewStyles = StyleSheet.create({
  throbber: {
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
  },
});

export default React.memo(ReviewBadge);

let RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;

export function mock(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function MockRealtimeUpdateSubscriptionWrapper(props: any): null | JSX.Element {
    return props.children;
  }
  RealtimeUpdateSubscriptionWrapper = MockRealtimeUpdateSubscriptionWrapper;
}

export function restore(): void {
  RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;
}
